import { useContext, useState, useEffect, useRef } from 'react'
import { animated, useSpring, config } from 'react-spring'
import moment from 'moment'

import { useReportPageContext } from '../../contexts/report-page.context'
import { UserContext } from '../../../common/contexts/userContext'
import {
  FilterContainer,
  Filter,
  SliderFilter,
  ThresholdFilter,
  FILTER_SIZES
} from '../filter'
import { reportDataSetService } from '../../editor/report-data-set.service'
import { reportLayoutService } from '../report-layout.service'

import { ExportToggle } from './action/export-toggle'
import { TableToggle } from './action/table-toggle'
import { FilterToggle } from './action/filter-toggle'
import { LABEL, SLIDER_VALUES } from './constants'
import { useStyles } from './style'

export const ControlBar = function ({
  className = '',
  displayOnly = false,
  controlLayout = displayOnly ? 'flex' : 'grid',
  strataDefinition,
  sliderDefinition,
  filterDefinition,
  parentControlBar,
  parentBlock,
  parentBlockSelections,
  tableToggle = { enabled: false, on: false },
  exportToggle = { enabled: false, on: false },
  alwaysShowSelectDropdown = false,
  onChange = () => {},
  onToggle = () => {}
}) {
  const classes = useStyles()

  const user = useContext(UserContext)
  if (exportToggle.enabled && !user.hasPermission('feature:export-chart')) {
    // User must have feature enabled to export charts
    exportToggle.enabled = false
  }

  const [primaryFilter, setPrimaryFilter] = useState(getPrimaryFilter())
  const [filters, setFilters] = useState()
  const [thresholdFilters, setThresholdFilters] = useState()
  const [isFilterButtonToggled, setIsFilterButtonToggled] = useState(false)
  const [filterHeight, setFilterHeight] = useState(0)
  const reportPageContext = useReportPageContext()
  const parentControlBarState = getParentControlBarState()

  const useDeprecatedDisplayProp = primaryFilter?.useDeprecatedDisplayProp
  const selectedPrimaryFilter = useRef()
  const selectedStrata = useRef(getDefaultStrataSelection(strataDefinition))
  const selectedSlider = useRef(getDefaultSliderSelection(sliderDefinition))
  const selectedFilters = useRef({})
  const selectedThresholdFilters = useRef({})
  const chartPropOverrides = useRef({})
  const prevFilterHeight = useRef(0)

  const otherFilters = filters?.filter((filter) => (
    !filter.isPrimary && (alwaysShowSelectDropdown || filter.options.filter(({ hide }) => (!hide)).length > 1))
  ) || []

  const hasPrimaryFilter = primaryFilter?.options && primaryFilter.options.length > 1
  const hasMultipleStrata = strataDefinition?.options && strataDefinition.options.length > 1
  const hasSliderDefinition = !!sliderDefinition
  const hasPrimaryControls = hasPrimaryFilter || hasMultipleStrata || hasSliderDefinition
  const hasOtherFilters = otherFilters?.length > 0 || thresholdFilters?.length > 0
  const hasActions = !displayOnly

  const isControllable = hasPrimaryControls || hasOtherFilters || exportToggle.enabled || tableToggle.enabled
  const isFilterDrawerVisible = !displayOnly && hasPrimaryControls && hasOtherFilters
  const isInteractive = reportPageContext.page.isInteractive && isControllable

  const filterContainerStyles = useSpring({
    config: { ...config.stiff, clamp: true },
    from: {
      height: prevFilterHeight.current
    },
    to: {
      height: filterHeight
    }
  })

  function callOnChange ({ display = false, strata = false, filters = false, slider = false, threshold = false, props = false, tableToggle = false }, selectionOverrides = {}) {
    // If we are expecting parent selections, but have not received them yet, hold off on calling onChange
    if ((parentControlBar && !parentControlBarState) || (parentBlock && !parentBlockSelections)) {
      return
    }
    const selections = Object.assign(getSelections(), selectionOverrides)
    const changes = { display, strata, filters, slider, threshold, props, tableToggle }

    onChange(selections, changes)
  }

  function onStrataChange (value, label) {
    selectedStrata.current = {
      value,
      label
    }

    initializeFilters()

    callOnChange({ strata: true, filters: true, threshold: true })
  }

  function onSliderChange (value, label) {
    // onChange is called on every mouse move
    value = sliderDefinition.dateMode ? moment.utc(value).toISOString() : value
    if (value !== selectedSlider.current?.value) {
      selectedSlider.current = {
        key: sliderDefinition && sliderDefinition.key,
        value,
        label
      }
      callOnChange({ slider: true })
    }
  }

  function onFilterChange (key, value, label) {
    let isPrimarySelected = false

    const selected = {
      value,
      label
    }
    const updatedFilters = filters.map((filter) => {
      if (filter.key === key) {
        filter.selected = selected
        if (filter.isPrimary) {
          selectedPrimaryFilter.current = selected
          isPrimarySelected = true
        }
      }
      return filter
    })
    setFilters(updatedFilters)

    selectedFilters.current[key] = selected

    const isDisplaySelected = isPrimarySelected && useDeprecatedDisplayProp

    updateChartProps()
    callOnChange({
      display: isDisplaySelected,
      filters: !isDisplaySelected
    })
  }

  function onThresholdFilterChange (dataSource, key, thresholdKey, thresholdValue, thresholdLabel, { committed = true } = {}) {
    const updatedThresholdFilters = thresholdFilters.map((thresholdFilter) => {
      if (thresholdFilter.key === key) {
        thresholdFilter.selected = {
          key: thresholdKey,
          value: thresholdValue,
          label: thresholdLabel
        }
      }
      return thresholdFilter
    })
    setThresholdFilters(updatedThresholdFilters)

    selectedThresholdFilters.current[key] = {
      dataSource,
      key: thresholdKey,
      value: thresholdValue,
      label: thresholdLabel
    }

    if (committed) {
      callOnChange({ threshold: true })
    }
  }

  function onResetFilters () {
    initializeFilters({ includePrimary: false })

    callOnChange({ filters: true, threshold: true })
  }

  function onExportToggle () {
    onToggle({ type: 'export', on: !exportToggle.on })
  }

  function onTableToggle () {
    onToggle({ type: 'table', on: !tableToggle.on })
  }

  function onFilterToggle () {
    setIsFilterButtonToggled(!isFilterButtonToggled)
  }

  function getPrimaryFilter () {
    if (!filterDefinition || !Array.isArray(filterDefinition.fields)) {
      return
    }

    const primaryFilter = filterDefinition.fields.find(({ isPrimary }) => isPrimary)
    if (primaryFilter?.options.filter(({ hide }) => (!hide)).length > 1) {
      return
    }

    return primaryFilter
  }

  function getFiltersWithDefaultValues ({ includePrimary = true } = {}) {
    if (!filterDefinition || !Array.isArray(filterDefinition.fields)) {
      return
    }

    const updatedFilters = filterDefinition.fields
      .filter(({ isSplitChartFilter }) => !isSplitChartFilter)
      .map((filter) => {
        // Reset all filters to their default value
        if (!filter.isPrimary || includePrimary) {
          filter.selected = getDefaultFilterSelection(filter)
        }
        return filter
      })

    // Hide the filter if needed
    return updatedFilters.filter((filter) => {
      // Hide if a strata or primary filter condition is specified
      if (filter.hideCondition) {
        if ((filter.hideCondition.strata || []).includes(selectedStrata.current?.value)) {
          return false
        }
        if ((filter.hideCondition.primaryFilter || []).includes(selectedPrimaryFilter.current?.value)) {
          return false
        }
      }
      // Hide if stratifying by the same field as this filter
      return (filter.key !== selectedStrata.current?.value)
    })
  }

  function getThresholdFiltersWithDefaultValues () {
    // Clear threshold filter
    return filterDefinition?.thresholdFilters?.fields?.map((filter) => {
      filter.isThreshold = true
      filter.selected = getDefaultThresholdFilterSelection(filter)

      return filter
    })
  }

  function getDefaultStrataSelection (strataDefinition) {
    const value = reportLayoutService.getDefaultValue(strataDefinition)
    if (value === undefined) {
      return
    }

    return {
      value,
      label: reportDataSetService.getFilterOptionLabel(strataDefinition, value)
    }
  }

  function getDefaultSliderSelection (sliderDefintion) {
    const value = reportLayoutService.getDefaultValue(sliderDefintion)
    if (value === undefined) {
      return
    }

    return {
      key: sliderDefinition && sliderDefinition.key,
      value,
      label: reportDataSetService.getSliderFilterOptionLabel(sliderDefintion, value)
    }
  }

  function getDefaultThresholdFilterSelection (filter) {
    const key = reportLayoutService.getDefaultValue(filter)
    const value = SLIDER_VALUES[0].value
    if (key === undefined || value === undefined) {
      return
    }

    return {
      key,
      value,
      label: reportDataSetService.getThresholdFilterOptionLabel(filter, key, value)
    }
  }

  function getDefaultFilterSelection (filter) {
    if (!filter) {
      return
    }

    // Check if the parent has sent in a value and default to that
    const parentFilterSelection = ((parentBlockSelections || {}).filters || {})[filter.key]
    if (parentFilterSelection !== undefined) {
      return parentFilterSelection
    }

    const value = reportLayoutService.getDefaultValue(filter)
    return {
      value,
      label: reportDataSetService.getFilterOptionLabel(filter, value)
    }
  }

  function updateChartProps () {
    if (selectedPrimaryFilter.current) {
      const selectedOption = primaryFilter?.options.find(({ value }) => value === selectedPrimaryFilter.current?.value)
      if (selectedOption && selectedOption.propOverrides) {
        chartPropOverrides.current = reportLayoutService.applyPropOverrides(chartPropOverrides.current, selectedOption.propOverrides)
      }
    }
  }

  function hasChartPropOverrides () {
    if (!primaryFilter || !Array.isArray(primaryFilter.options)) {
      return false
    }

    const selectedOption = primaryFilter.options.find(({ value }) => value === selectedPrimaryFilter.current?.value)
    return selectedOption && selectedOption.propOverrides
  }

  function getParentControlBarState () {
    if (!parentControlBar || !reportPageContext || !reportPageContext.getControlBarState) {
      return null
    }

    return reportPageContext.getControlBarState(parentControlBar.key)
  }

  /**
   * Only filters on parent selections are supported - strata, slider, etc. can be pulled from parent later if needed
   */
  function getParentSelections () {
    let parentSelections = { filters: {} }

    // get parent control bar filters
    if (parentControlBar && parentControlBarState) {
      if (parentControlBar.filters && parentControlBarState.selections) {
        parentControlBar.filters.forEach((filter) => {
          const filterSelection = parentControlBarState.selections.filters[filter]
          if (filterSelection !== undefined) {
            parentSelections.filters[filter] = filterSelection
          }
        })
      }
    }

    // get parent block filters (overrides parent control bar if there are conflicts)
    if (parentBlock && parentBlockSelections) {
      if (parentBlockSelections.filters) {
        parentSelections.filters = { ...parentSelections.filters, ...parentBlockSelections.filters }
      }
    }

    return parentSelections
  }

  function getSelections () {
    const parent = getParentSelections()

    const parentFilters = parent.filters || {}
    // Selected filters override parent filters if there are conflicts
    const filters = { ...parentFilters, ...selectedFilters.current }

    // Assign the primary filter value to display, and delete from filters if this is the legacy display type filter
    let display
    if (primaryFilter && useDeprecatedDisplayProp) {
      display = filters[primaryFilter.key]

      delete filters[primaryFilter.key]
    }

    const thresholdFilters = selectedThresholdFilters.current

    return {
      display,
      strata: selectedStrata.current,
      filters,
      thresholdFilters,
      refDataSources: thresholdFilters && Object.values(thresholdFilters).map(({ dataSource }) => dataSource),
      slider: selectedSlider.current,
      props: { ...chartPropOverrides.current }
    }
  }

  function initializeFilters ({ includePrimary = true } = {}) {
    const filters = getFiltersWithDefaultValues({ includePrimary })
    if (filters) {
      const initializedFilters = {}
      filters.forEach((filter) => {
        if (filter.isPrimary) {
          selectedPrimaryFilter.current = filter.selected
          setPrimaryFilter(filter)
        }

        if (filter.selected !== undefined) {
          initializedFilters[filter.key] = filter.selected
        }
      })
      selectedFilters.current = initializedFilters

      setFilters(filters)
    }

    const thresholdFilters = getThresholdFiltersWithDefaultValues()
    if (thresholdFilters) {
      thresholdFilters.forEach(({ key, dataSource, selected }) => {
        selectedThresholdFilters.current[key] = {
          dataSource,
          ...selected
        }
      })

      setThresholdFilters(thresholdFilters)
    }
  }

  useEffect(() => {
    initializeFilters()
    updateChartProps()

    const selections = getSelections()
    callOnChange({
      display: !!selections.display,
      filters: !!selections.filters,
      strata: !!selections.strata?.value,
      threshold: !!selections.threshold,
      props: hasChartPropOverrides()
    })
  }, [])

  useEffect(() => {
    if (parentControlBarState) {
      callOnChange(parentControlBarState.changes || {})
    }
  }, [ parentControlBarState ])

  useEffect(() => {
    if (parentBlockSelections) {
      const parentChanges = Object.keys(parentBlockSelections)
        .reduce((parentChanges, key) => ({ ...parentChanges, [key]: true }), {})
      callOnChange(parentChanges)
    }
  }, [ parentBlockSelections ])

  useEffect(() => {
    if (!filters || !filterDefinition || !filterDefinition.fields || filterDefinition.fields.length === 0) {
      return
    }

    const previouslySelectedPrimaryFilter = selectedPrimaryFilter.current
    const previouslySelectedFilters = { ...selectedFilters.current }

    initializeFilters()

    let hasFiltersChanged = false
    if (Object.keys(previouslySelectedFilters).length === Object.keys(selectedFilters.current).length) {
      for (let key in previouslySelectedFilters) {
        if (previouslySelectedFilters[key]?.value !== selectedFilters.current[key]?.value) {
          hasFiltersChanged = true
        }
      }
    } else {
      hasFiltersChanged = true
    }

    if (hasFiltersChanged || (previouslySelectedPrimaryFilter?.value !== selectedPrimaryFilter.current?.value)) {
      callOnChange({ filters: true })
    }
  }, [ filterDefinition ])

  useEffect(() => {
    prevFilterHeight.current = filterHeight
  }, [filterHeight])

  if (!isInteractive) {
    return null
  }

  return <div data-qa="control-bar" className={`${className} ${classes.container}`}>
    <FilterContainer layout={controlLayout}>
      {hasPrimaryFilter &&
        <Filter
          key={primaryFilter.key}
          filter={primaryFilter}
          size={FILTER_SIZES.LARGE}
          displayOnly={displayOnly}
          onChange={onFilterChange}
        />
      }
      {hasMultipleStrata &&
        <Filter
          filter={{
            key: 'stratify-by',
            label: LABEL.STRATA,
            options: strataDefinition.options,
            selected: selectedStrata.current
          }}
          size={FILTER_SIZES.LARGE}
          displayOnly={displayOnly}
          onChange={(key, value, label) => onStrataChange(value, label)}
        />
      }
      {sliderDefinition && sliderDefinition.options &&
        <SliderFilter
          filter={{
            key: 'slider',
            label: sliderDefinition.label,
            selected: selectedSlider.current
          }}
          size={FILTER_SIZES.LARGE}
          dateMode={sliderDefinition.dateMode}
          dateValueFormat={sliderDefinition.dateValueFormat}
          min={reportLayoutService.getMinValue(sliderDefinition)}
          max={reportLayoutService.getMaxValue(sliderDefinition)}
          marks={reportLayoutService.getSliderMarkerPoints(sliderDefinition)}
          displayOnly={displayOnly}
          onChange={(key, value, label) => onSliderChange(value, label)}
        />
      }
      {!isFilterDrawerVisible && (
        <Filters
          filters={otherFilters}
          thresholdFilters={thresholdFilters}
          displayOnly={displayOnly}
          onFilterChange={onFilterChange}
          onThresholdFilterChange={onThresholdFilterChange}
        />
      )}
      {hasActions && (
        <div className={classes.actions}>
          {exportToggle.enabled && !tableToggle.on && (
            <ExportToggle
              isOn={exportToggle.on}
              onChange={() => onExportToggle(!exportToggle.on)}
            />
          )}
          {tableToggle.enabled && (
            <TableToggle
              isOn={tableToggle.on}
              iconOn={tableToggle.iconOn}
              iconOff={tableToggle.iconOff}
              tooltipOn={tableToggle.tooltipOn}
              tooltipOff={tableToggle.tooltipOff}
              onChange={onTableToggle}
            />
          )}
          {isFilterDrawerVisible &&
            <FilterToggle
              isOn={isFilterButtonToggled}
              tooltipOn={thresholdFilters?.toggledButtonLabel}
              tooltipOff={thresholdFilters?.buttonLabel}
              onChange={onFilterToggle}
            />
          }
        </div>
      )}
    </FilterContainer>
    {isFilterDrawerVisible && (
      <animated.div style={{
        ...filterContainerStyles
      }}>
        <div
          ref={(ref) => setFilterHeight(ref?.offsetHeight)}
          className={isFilterButtonToggled ? classes.borderedFilterContainer : ''}
        >
          <FilterContainer
            layout={!isFilterButtonToggled ? 'flex' : controlLayout}
            showReset={isFilterButtonToggled}
            onReset={onResetFilters}
          >
            <Filters
              filters={otherFilters}
              thresholdFilters={thresholdFilters}
              displayOnly={displayOnly || !isFilterButtonToggled}
              onFilterChange={onFilterChange}
              onThresholdFilterChange={onThresholdFilterChange}
            />
          </FilterContainer>
        </div>
      </animated.div>
    )}
  </div>
}

function Filters ({
  filters,
  thresholdFilters,
  displayOnly = false,
  onFilterChange,
  onThresholdFilterChange
}) {
  return <>
    {filters.map((filter) => (
      <Filter
        key={filter.key}
        filter={filter}
        size={FILTER_SIZES.LARGE}
        displayOnly={displayOnly}
        onChange={onFilterChange}
      />
    ))}
    {thresholdFilters?.map((filter) => (
      <ThresholdFilter
        key={filter.key}
        filter={filter}
        thresholds={SLIDER_VALUES}
        size={FILTER_SIZES.LARGE}
        displayOnly={displayOnly}
        onChange={onThresholdFilterChange}
        onThresholdChange={(dataSource, key, thresholdKey, thresholdValue, thresholdLabel) => {
          onThresholdFilterChange(dataSource, key, thresholdKey, thresholdValue, thresholdLabel, { committed: false })
        }}
        onThresholdCommitted={onThresholdFilterChange}
      />
    ))}
  </>
}
